import React, { useState, useRef } from 'react';
import { Upload, Download, FileSpreadsheet, FileJson, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ImportExport() {
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const excelInputRef = useRef(null);
  const jsonInputRef = useRef(null);

  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setMessage({ type: '', text: '' });

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/import/excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMessage({ type: 'success', text: response.data.message });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Error importing Excel file' 
      });
    } finally {
      setImporting(false);
      if (excelInputRef.current) excelInputRef.current.value = '';
    }
  };

  const handleJsonImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setMessage({ type: '', text: '' });

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/import/json`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMessage({ type: 'success', text: response.data.message });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Error importing JSON file' 
      });
    } finally {
      setImporting(false);
      if (jsonInputRef.current) jsonInputRef.current.value = '';
    }
  };

  const handleExcelExport = async () => {
    setExporting(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.get(`${API_URL}/api/export/excel`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoices_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: 'Excel file downloaded successfully!' });
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'Error exporting to Excel' 
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="import-export-page" className="space-y-6 animate-fade-in max-w-4xl">
      {/* Message */}
      {message.text && (
        <div className={`px-4 py-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          {message.text}
          <button 
            onClick={() => setMessage({ type: '', text: '' })}
            className="ml-auto text-lg leading-none"
          >
            ×
          </button>
        </div>
      )}

      {/* Import Section */}
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-6">
        <h2 className="text-lg font-semibold text-zinc-900 mb-2">Import Data</h2>
        <p className="text-sm text-zinc-500 mb-6">
          Import your existing invoice data from Excel or JSON files. The system will automatically map columns to invoice fields.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Excel Import */}
          <div className="border border-zinc-200 rounded-lg p-6 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <FileSpreadsheet className="w-6 h-6 text-emerald-700" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900">Excel / CSV Import</h3>
                <p className="text-xs text-zinc-500">.xlsx, .xls, .csv files</p>
              </div>
            </div>
            <p className="text-sm text-zinc-600 mb-4">
              Import invoices from Excel or CSV. Supported columns: Invoice No, Customer Name, Contact, Grand Total, Date, Type, etc.
            </p>
            <input
              ref={excelInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleExcelImport}
              className="hidden"
              id="excel-import"
            />
            <label
              htmlFor="excel-import"
              data-testid="excel-import-btn"
              className={`flex items-center justify-center gap-2 w-full px-4 py-2 border-2 border-dashed border-zinc-300 rounded-lg cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition-colors ${
                importing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {importing ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Upload className="w-5 h-5" />
              )}
              <span className="font-medium text-sm">
                {importing ? 'Importing...' : 'Choose Excel File'}
              </span>
            </label>
          </div>

          {/* JSON Import */}
          <div className="border border-zinc-200 rounded-lg p-6 hover:border-blue-300 hover:bg-blue-50/30 transition-colors">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileJson className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900">JSON Import</h3>
                <p className="text-xs text-zinc-500">.json files</p>
              </div>
            </div>
            <p className="text-sm text-zinc-600 mb-4">
              Import from JSON file. Can be a single invoice object or an array of invoices.
            </p>
            <input
              ref={jsonInputRef}
              type="file"
              accept=".json"
              onChange={handleJsonImport}
              className="hidden"
              id="json-import"
            />
            <label
              htmlFor="json-import"
              data-testid="json-import-btn"
              className={`flex items-center justify-center gap-2 w-full px-4 py-2 border-2 border-dashed border-zinc-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors ${
                importing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {importing ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Upload className="w-5 h-5" />
              )}
              <span className="font-medium text-sm">
                {importing ? 'Importing...' : 'Choose JSON File'}
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Export Section */}
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-6">
        <h2 className="text-lg font-semibold text-zinc-900 mb-2">Export Data</h2>
        <p className="text-sm text-zinc-500 mb-6">
          Download all your invoice data as an Excel file for backup or analysis in other tools.
        </p>

        <button
          data-testid="excel-export-btn"
          onClick={handleExcelExport}
          disabled={exporting}
          className="flex items-center gap-2 px-6 py-3 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors font-medium disabled:opacity-50"
        >
          {exporting ? (
            <Loader className="w-5 h-5 animate-spin" />
          ) : (
            <Download className="w-5 h-5" />
          )}
          {exporting ? 'Exporting...' : 'Export to Excel'}
        </button>
      </div>

      {/* Import Template Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-amber-800 mb-2">Excel Import Template</h3>
        <p className="text-sm text-amber-700 mb-3">
          For best results, your Excel file should include these columns:
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-amber-700">
          <span className="bg-amber-100 px-2 py-1 rounded">invoice_number</span>
          <span className="bg-amber-100 px-2 py-1 rounded">invoice_type</span>
          <span className="bg-amber-100 px-2 py-1 rounded">invoice_date</span>
          <span className="bg-amber-100 px-2 py-1 rounded">customer_name</span>
          <span className="bg-amber-100 px-2 py-1 rounded">customer_contact</span>
          <span className="bg-amber-100 px-2 py-1 rounded">customer_address</span>
          <span className="bg-amber-100 px-2 py-1 rounded">item_name</span>
          <span className="bg-amber-100 px-2 py-1 rounded">quantity</span>
          <span className="bg-amber-100 px-2 py-1 rounded">sale_price</span>
          <span className="bg-amber-100 px-2 py-1 rounded">grand_total</span>
          <span className="bg-amber-100 px-2 py-1 rounded">financer_name (EMI)</span>
          <span className="bg-amber-100 px-2 py-1 rounded">emi_months (EMI)</span>
        </div>
      </div>
    </div>
  );
}

export default ImportExport;
